import React, { useEffect, useState, useRef } from 'react';
import { Gift, Calendar, Share2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getRegistries } from '../lib/registry';
import type { Registry, RegistryItem } from '../types/registry';
import ShareRegistryModal from '../components/ShareRegistryModal';

const RegistryList = () => {
  const [registries, setRegistries] = useState<(Registry & { items: RegistryItem[] })[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRegistryId, setSelectedRegistryId] = useState<string | null>(null);
  const sparklesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadRegistries();

    const createSparkle = () => {
      if (!sparklesRef.current) return;
      
      const sparkle = document.createElement('div');
      sparkle.className = 'sparkle';
      
      const startX = Math.random() * window.innerWidth;
      const endX = startX + (Math.random() * 100 - 50);
      
      sparkle.style.setProperty('--start-x', `${startX}px`);
      sparkle.style.setProperty('--end-x', `${endX}px`);
      sparkle.style.left = `${startX}px`;
      
      sparklesRef.current.appendChild(sparkle);
      
      setTimeout(() => sparkle.remove(), 3000);
    };

    const interval = setInterval(createSparkle, 200);

    for (let i = 0; i < 10; i++) {
      setTimeout(createSparkle, i * 100);
    }

    return () => clearInterval(interval);
  }, []);

  const loadRegistries = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getRegistries();
      setRegistries(data);
    } catch (err) {
      console.error('Error loading registries:', err);
      setError('Failed to load registries');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-10">
      {/* Sparkles Container */}
      <div ref={sparklesRef} className="sparkles-container" />

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 animate-fade-in">
        <div>
          <h1 className="text-4xl font-display font-bold text-white text-shadow-lg">Your Registries</h1>
          <p className="text-white/70 mt-3 font-body text-lg">Manage and share your elegant gift registries</p>
        </div>
        <Link
          to="/create"
          className="btn-luxury group"
        >
          <Gift className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" />
          <span>Create New Registry</span>
        </Link>
      </div>

      {error && (
        <div className="bg-red-500/20 border border-red-500/30 text-red-200 px-6 py-4 rounded-xl backdrop-blur-sm">
          {error}
        </div>
      )}

      {registries.length === 0 ? (
        <div className="text-center py-16 bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg animate-scale-in">
          <Gift className="w-20 h-20 mx-auto mb-8 text-luxury-gold-400" />
          <h3 className="text-2xl font-display font-semibold text-white mb-4">No Registries Yet</h3>
          <p className="text-white/70 mb-8 font-body text-lg max-w-md mx-auto">Create your first elegant registry to start collecting meaningful gifts!</p>
          <Link
            to="/create"
            className="btn-luxury group inline-flex"
          >
            <span className="mr-2">Create Your First Registry</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {registries.map((registry) => (
            <div key={registry.id} className="group bg-gray-800/50 backdrop-blur-xl border border-white/20 rounded-3xl shadow-soft-lg overflow-hidden hover:scale-105 transition-all duration-500 animate-fade-in">
              <div className="p-8 space-y-6">
                <div>
                  <h3 className="text-xl font-display font-semibold text-white mb-2">{registry.title}</h3>
                  <div className="w-12 h-1 bg-gradient-to-r from-luxury-gold-400 to-luxury-gold-600 rounded-full"></div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center text-white/70 font-body">
                    <Calendar className="w-5 h-5 mr-2" />
                    <span>{new Date(registry.event_date).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center text-white/70 font-body">
                    <Gift className="w-5 h-5 mr-2" />
                    <span>
                      {registry.items.length} items (
                      {registry.items.filter(item => item.is_claimed).length} claimed)
                    </span>
                  </div>
                </div>

                {registry.description && (
                  <p className="text-white/60 text-sm line-clamp-2 font-body">
                    {registry.description}
                  </p>
                )}

                <div className="flex justify-between items-center pt-6 border-t border-white/10">
                  <button 
                    onClick={() => setSelectedRegistryId(registry.id)}
                    className="text-white/70 hover:text-white flex items-center space-x-2 font-body transition-colors duration-300"
                  >
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                  <Link
                    to={`/registries/${registry.id}`}
                    className="px-6 py-3 bg-luxury-gold-500 text-white rounded-xl hover:bg-luxury-gold-600 transition-all duration-300 font-body font-medium group-hover:scale-105"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedRegistryId && (
        <ShareRegistryModal
          registryId={selectedRegistryId}
          onClose={() => setSelectedRegistryId(null)}
        />
      )}
    </div>
  );
};

export default RegistryList;